/**
 * Admission price display.
 *
 * A missing price is not a free event — most organizers simply never fill it
 * in — so "not listed" and "Free" stay distinguishable everywhere they show.
 */

function formatAmount(amount: number): string {
  // Whole dollars stay whole: "$15", not "$15.00". Cents show when there are
  // any, since a $12.50 cover rounded to $13 is a wrong price, not a tidy one.
  return Number.isInteger(amount)
    ? `$${amount}`
    : `$${amount.toFixed(2)}`;
}

/** The unit assumed when an event does not name one. */
export const DEFAULT_PRICE_UNIT = "person";

/** "Free", "$15", "$10–$20", or null when no price was given. */
export function formatPrice(
  priceMin?: number,
  priceMax?: number
): string | null {
  if (priceMin === undefined && priceMax === undefined) return null;

  // A range needs both ends; with only a max, the max is the price.
  if (priceMin === undefined) return formatAmount(priceMax!);

  if (priceMax === undefined || priceMax === priceMin) {
    return priceMin === 0 ? "Free" : formatAmount(priceMin);
  }

  return `${formatAmount(priceMin)}–${formatAmount(priceMax)}`;
}

/**
 * The unit, but only when it is worth saying.
 *
 * Per-person is what everyone assumes, so spelling it out on every card is
 * noise — while "per couple" on a $100 lesson is the whole point. Returns null
 * when there is nothing surprising to report.
 */
export function priceUnitSuffix(
  priceUnit?: string,
  priceMin?: number
): string | null {
  // Free for one is free for everyone; a unit would only confuse.
  if (priceMin === 0 && priceUnit === undefined) return null;

  const unit = priceUnit?.trim();
  if (!unit || unit.toLowerCase() === DEFAULT_PRICE_UNIT) return null;
  return `per ${unit}`;
}

/** The price with its unit spelled out in full: "$100 per couple". */
export function formatPriceWithUnit(
  priceMin?: number,
  priceMax?: number,
  priceUnit?: string
): string | null {
  const price = formatPrice(priceMin, priceMax);
  if (price === null || price === "Free") return price;

  const unit = priceUnit?.trim() || DEFAULT_PRICE_UNIT;
  return `${price} per ${unit}`;
}
